import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import {
  BELOW_COLOR,
  QUESTION_COLORS,
  SCORE_COLOR,
} from "./readinessChartTheme";
import { sectionTranslator, useIntensity } from "./readinessFormat";

const tr = sectionTranslator("chart");

// The small pieces of the score chart: the dots recharts asks for, one legend entry and the
// "+ Session RPE / + HRV / + Resting HR" switch.

export const ScoreDot = ({ cx, cy, payload }) => {
  if (cx == null || cy == null || payload?.total == null) return null;
  const below = payload.status === "below";
  return (
    <circle
      cx={cx}
      cy={cy}
      r={below ? 5 : 4}
      fill={below ? BELOW_COLOR : SCORE_COLOR}
    />
  );
};

ScoreDot.propTypes = {
  cx: PropTypes.number,
  cy: PropTypes.number,
  payload: PropTypes.object,
};

// A dot of the overlay line: a plain solid circle. Recharts hands the line's dash pattern to its
// own dots, which would draw the ring broken.
export const OverlayDot = ({ cx, cy, value, color, radius = 3.5 }) => {
  if (cx == null || cy == null || value == null) return null;
  return (
    <circle
      cx={cx}
      cy={cy}
      r={radius}
      fill={color}
      stroke="var(--rr-surface)"
      strokeWidth={1.5}
    />
  );
};

OverlayDot.propTypes = {
  cx: PropTypes.number,
  cy: PropTypes.number,
  value: PropTypes.number,
  color: PropTypes.string.isRequired,
  radius: PropTypes.number,
};

export const LegendItem = ({ kind, color, label }) => (
  <span className="rr-legend__item">
    <span
      className={`rr-legend__mark rr-legend__mark--${kind}`}
      style={color ? { "--rr-mark": color } : undefined}
    />
    <span>{label}</span>
  </span>
);

LegendItem.propTypes = {
  kind: PropTypes.string.isRequired,
  color: PropTypes.string,
  label: PropTypes.string.isRequired,
};

// One toggle per question, each with its color: any number of them can be on (at least one)
export const QuestionSwitch = ({ options, shown, onToggle }) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <fieldset className="rr-segmented">
      <legend className="sr-only">
        {t("readinessRate.report.byQuestion.chart.aria")}
      </legend>
      {options.map((key) => {
        const on = shown.includes(key);
        return (
          <button
            key={key}
            type="button"
            className={on ? "active" : undefined}
            aria-pressed={on}
            onClick={() => onToggle(key)}
          >
            <i
              className="rr-segmented__swatch"
              style={{ "--rr-mark": QUESTION_COLORS[key] }}
            />
            <span>{t(`readinessRate.${key}`)}</span>
          </button>
        );
      })}
    </fieldset>
  );
};

QuestionSwitch.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  shown: PropTypes.arrayOf(PropTypes.string).isRequired,
  onToggle: PropTypes.func.isRequired,
};

export const OverlaySwitch = ({ options, value, onChange }) => {
  const { t } = useTranslation("athlete-portal");
  const { context } = useIntensity();
  return (
    <fieldset className="rr-segmented">
      <legend className="sr-only">{tr(t, "overlayAria")}</legend>
      {options.map((option) => (
        <button
          key={option}
          type="button"
          className={option === value ? "active" : undefined}
          aria-pressed={option === value}
          onClick={() => onChange(option)}
        >
          <span>
            {option === "none" ? "" : "+ "}
            {tr(t, `overlays.${option}`, { context })}
          </span>
        </button>
      ))}
    </fieldset>
  );
};

OverlaySwitch.propTypes = {
  options: PropTypes.arrayOf(PropTypes.string).isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
};
